import { useState, type FormEvent } from 'react';
import { CloseIcon, DefineToolIcon, EntryIcon, StatusCheckIcon, StatusNotReadyIcon } from '@/icons';
import { NumpadKeyboard } from '@/components/jog/NumpadKeyboard';
import { ToolSelect, type SelectOption } from '@/components/ToolsPage/ToolSelect';
import { ToolSavedModal } from '@/components/ToolsPage/ToolSavedModal';
import {
  TOOL_ART,
  TOOL_TYPES,
  FLUTE_OPTIONS,
  largeArt,
  numericPart,
  formatMm,
  formatMeasuredAt,
  isMeasured,
  type ToolRow,
  type ToolType,
} from '@/components/ToolsPage/toolData';
import styles from '@/components/ToolsPage/ToolFormModal.module.css';

interface ToolFormModalProps {
  readonly mode: 'edit' | 'define';
  readonly tool: ToolRow;
  /** Persists the saved row upstream (table JSON + G10 handoff to LinuxCNC). */
  readonly onSave: (tool: ToolRow) => void;
  readonly onClose: () => void;
}
type FormState = ToolRow;

const BLANK_TOOL: FormState = {
  no: '',
  type: '',
  diameter: '',
  helix: '',
  flute: '',
  offsetStatus: 'unMeasured',
};

// Tool Type dropdown reuses the table thumbnails as option icons.
const TYPE_OPTIONS: SelectOption[] = TOOL_TYPES.map((type) => ({
  value: type,
  label: type,
  icon: TOOL_ART[type].thumb,
}));

const FLUTE_SELECT_OPTIONS: SelectOption[] = FLUTE_OPTIONS.map((flute) => ({
  value: flute,
  label: flute,
}));

export function ToolFormModal({ mode, tool, onSave, onClose }: ToolFormModalProps): JSX.Element {
  const isEdit = mode === 'edit';
  const [form, setForm] = useState<FormState>(isEdit ? tool : BLANK_TOOL);
  const [numpadField, setNumpadField] = useState<'diameter' | 'toolLength' | 'helix' | null>(null);
  // Live numpad value; form.<field> stays untouched until Go commits.
  const [draft, setDraft] = useState('');
  const [saved, setSaved] = useState(false);
  // Blocks save; shown as an in-modal warning until the values are corrected.
  const [error, setError] = useState<string | null>(null);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]): void => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setError(null);
  };

  const openNumpad = (field: 'diameter' | 'toolLength' | 'helix'): void => {
    setDraft('');
    setNumpadField(field);
  };

  // Save gate: only Tool Type, diameter and tool length are required; helix and
  // flute count are optional.
  const canSave = form.type !== '' && form.diameter !== '' && (form.toolLength ?? '') !== '';

  const handleSubmit = (e: FormEvent): void => {
    e.preventDefault();
    // Geometry rule: a tool's helix can't run past its overall length. Only
    // enforce when both are numeric — a blank field isn't a violation.
    const helix = parseFloat(numericPart(form.helix));
    const len = parseFloat(numericPart(form.toolLength ?? ''));
    if (!Number.isNaN(helix) && !Number.isNaN(len) && helix > len) {
      setError('Helix length cannot be greater than tool length.');
      return;
    }
    // A fresh save invalidates any prior measurement — offset must be re-taken
    // via Call Tool → Get Offset, so it starts unmeasured with no timestamp.
    onSave({
      no: tool.no,
      type: form.type || tool.type,
      diameter: form.diameter,
      toolLength: form.toolLength,
      helix: form.helix,
      flute: form.flute,
      offsetStatus: 'unMeasured',
    });
    setSaved(true);
  };

  if (saved) {
    return <ToolSavedModal mode={mode} no={tool.no} tool={form} onClose={onClose} />;
  }

  return (
    <div
      className={styles.overlay}
      role="dialog"
      aria-modal="true"
      aria-label={isEdit ? 'Edit Tool' : 'Define Tool'}
      onClick={onClose}
    >
      <form className={styles.modal} onClick={(e) => e.stopPropagation()} onSubmit={handleSubmit}>
        <div className={styles.header}>
          <div className={styles.headerLead}>
            {isEdit ? (
              <EntryIcon width={24} height={24} className={styles.headerIcon} />
            ) : (
              <DefineToolIcon width={28} height={28} className={styles.headerIcon} />
            )}
            <div className={styles.headerText}>
              <span className={styles.title}>{isEdit ? 'Edit Tool' : 'Define Tool'}</span>
              <span className={styles.subtitle}>
                {isEdit
                  ? 'Edit tool information and parameters.'
                  : 'Define a new tool and add it to the list.'}
              </span>
            </div>
          </div>
          <button className={styles.close} onClick={onClose} type="button" aria-label="Close">
            <CloseIcon width={20} height={20} />
          </button>
        </div>

        <div className={styles.summary}>
          <div className={styles.summaryInfo}>
            <span className={styles.summaryLabel}>{isEdit ? 'Selected Tool' : 'New Tool'}</span>
            <span className={styles.summaryNo}>{tool.no}</span>
            <span className={styles.summarySub}>{isEdit ? tool.type : 'New Tool Slot'}</span>
          </div>
          {form.type !== '' ? (
            <div className={styles.summaryArt}>
              <span className={styles.summaryGlow} />
              <img
                src={TOOL_ART[form.type].large ?? largeArt(form.type)}
                alt={form.type}
                className={styles.summaryIcon}
              />
            </div>
          ) : (
            <span className={styles.summaryPlaceholder} />
          )}
        </div>

        <div className={styles.section}>
          <span className={styles.sectionTitle}>Tool Information</span>
          <div className={styles.grid}>
            <div className={styles.field}>
              <span className={styles.fieldLabel}>Tool Type</span>
              <ToolSelect
                value={form.type}
                options={TYPE_OPTIONS}
                placeholder="Select Tool Type"
                onChange={(v) => set('type', v as ToolType)}
              />
            </div>

            <div className={styles.field}>
              <span className={styles.fieldLabel}>Ø (mm)</span>
              <div className={styles.unitInput}>
                <input
                  className={styles.unitField}
                  value={numpadField === 'diameter' ? draft || '0' : numericPart(form.diameter)}
                  onClick={() => openNumpad('diameter')}
                  placeholder="6.00"
                  readOnly
                />
                <span className={styles.unitChip}>mm</span>
              </div>
            </div>

            <div className={styles.field}>
              <span className={styles.fieldLabel}>Tool L. (mm)</span>
              <div className={styles.unitInput}>
                <input
                  className={styles.unitField}
                  value={
                    numpadField === 'toolLength' ? draft || '0' : numericPart(form.toolLength ?? '')
                  }
                  onClick={() => openNumpad('toolLength')}
                  placeholder="50.00"
                  readOnly
                />
                <span className={styles.unitChip}>mm</span>
              </div>
            </div>

            <div className={styles.field}>
              <span className={styles.fieldLabel}>Helix Lenght</span>
              <div className={styles.unitInput}>
                <input
                  className={styles.unitField}
                  value={numpadField === 'helix' ? draft || '0' : numericPart(form.helix)}
                  onClick={() => openNumpad('helix')}
                  placeholder="20.00"
                  readOnly
                />
                <span className={styles.unitChip}>mm</span>
              </div>
            </div>

            <div className={styles.field}>
              <span className={styles.fieldLabel}>Flue Count</span>
              <ToolSelect
                value={form.flute}
                options={FLUTE_SELECT_OPTIONS}
                placeholder="Select Flute Count"
                onChange={(v) => set('flute', v)}
              />
            </div>
          </div>
        </div>

        {isEdit && (
          <div className={styles.section}>
            <span className={styles.sectionTitle}>Offset Status</span>
            <div className={styles.offsetRow}>
              {isMeasured(tool) ? (
                <>
                  <span className={styles.offsetStatus}>
                    <StatusCheckIcon width={20} height={20} />
                    Measured
                  </span>
                  {tool.measuredAt && (
                    <span className={styles.offsetWhen}>
                      Last measured at: {formatMeasuredAt(tool.measuredAt)}
                    </span>
                  )}
                </>
              ) : (
                <span className={`${styles.offsetStatus} ${styles.offsetStatusNone}`}>
                  <StatusNotReadyIcon width={20} height={20} />
                  Not measured
                </span>
              )}
            </div>
          </div>
        )}

        {error && (
          <div className={styles.warning} role="alert">
            <StatusNotReadyIcon width={20} height={20} />
            {error}
          </div>
        )}

        <div className={styles.footer}>
          <button type="button" className={styles.btnGhost} onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className={styles.btnPrimary} disabled={!canSave}>
            {isEdit ? 'Save Changes' : 'Save Tool'}
          </button>
        </div>

        {numpadField && (
          <NumpadKeyboard
            label={
              numpadField === 'diameter'
                ? 'Diameter'
                : numpadField === 'toolLength'
                  ? 'Tool Length'
                  : 'Helix Length'
            }
            onChange={setDraft}
            onSubmit={(value) => set(numpadField, formatMm(value))}
            onClose={() => setNumpadField(null)}
            submitLabel="Set"
          />
        )}
      </form>
    </div>
  );
}
